// Altersfreigabe für Trainings-Buchungen (CoachDesk / Eltern-Portal).

/** Volljährigkeit ab 18 Jahren (Deutschland). */
export const ADULT_AGE_YEARS = 18;

/** @typedef {'adult'|'minor'|'unknown'} AgeVerificationStatus */

function __asStr(x) {
  return String(x ?? "").trim();
}

function __parseBirthDate(raw) {
  const s = __asStr(raw);
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (Number.isNaN(dt.getTime())) return null;
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function __parseBirthYear(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const y = Math.trunc(n);
  if (y < 1900 || y > 2100) return null;
  return y;
}

/**
 * Jahrgang aus ISO-Geburtsdatum (YYYY-MM-DD).
 * @param {string|null|undefined} birthDateRaw
 * @returns {number|null}
 */
export function deriveBirthYearFromBirthDate(birthDateRaw) {
  const birthDate = __parseBirthDate(birthDateRaw);
  if (!birthDate) return null;
  return birthDate.getFullYear();
}

/**
 * Exaktes Alter in vollen Jahren (Geburtstag muss im Referenzjahr erreicht sein).
 * @param {Date} birthDate
 * @param {Date} refDate
 */
export function computeAgeInFullYears(birthDate, refDate = new Date()) {
  if (!(birthDate instanceof Date) || Number.isNaN(birthDate.getTime())) return null;
  const ref = refDate instanceof Date && !Number.isNaN(refDate.getTime()) ? refDate : new Date();
  let age = ref.getFullYear() - birthDate.getFullYear();
  const refMonth = ref.getMonth();
  const birthMonth = birthDate.getMonth();
  if (refMonth < birthMonth || (refMonth === birthMonth && ref.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

/**
 * @param {object|null|undefined} person
 * @param {Date} [refDate]
 * @returns {AgeVerificationStatus}
 */
/**
 * @param {object|null|undefined} person
 */
export function normalizePersonBirthFieldsForAgeCheck(person) {
  const p = person && typeof person === "object" ? person : {};
  const birthDateRaw = p.birthDate ?? p.birth_date ?? null;
  const birthDate =
    typeof birthDateRaw === "string"
      ? __asStr(birthDateRaw).slice(0, 10)
      : birthDateRaw instanceof Date && !Number.isNaN(birthDateRaw.getTime())
        ? birthDateRaw.toISOString().slice(0, 10)
        : "";
  const birthYearRaw = p.birthYear ?? p.birth_year ?? null;
  return {
    ...p,
    birthDate: birthDate || null,
    birth_date: birthDate || null,
    birthYear: birthYearRaw,
    birth_year: birthYearRaw,
  };
}

export function getAgeVerificationStatus(person, refDate = new Date()) {
  const p = normalizePersonBirthFieldsForAgeCheck(person);
  const cached = __asStr(p.ageStatus ?? p.age_status).toLowerCase();
  if (cached === "adult" || cached === "minor") {
    return cached;
  }

  const birthDateRaw = p.birthDate ?? p.birth_date ?? null;
  const birthYearRaw = p.birthYear ?? p.birth_year ?? null;

  const birthDate = __parseBirthDate(birthDateRaw);
  if (birthDate) {
    const age = computeAgeInFullYears(birthDate, refDate);
    if (age == null) return "unknown";
    return age >= ADULT_AGE_YEARS ? "adult" : "minor";
  }

  const birthYear = __parseBirthYear(birthYearRaw);
  if (birthYear != null) {
    const ref = refDate instanceof Date && !Number.isNaN(refDate.getTime()) ? refDate : new Date();
    // Konservativ: Jahrgang gilt erst ab 01.01.(birthYear + 19) als volljährig.
    const adultFrom = new Date(birthYear + ADULT_AGE_YEARS + 1, 0, 1);
    if (ref >= adultFrom) return "adult";
    return "minor";
  }

  return "unknown";
}

const GUARDIAN_RELATIONSHIP_TYPES = Object.freeze([
  "parent",
  "mother",
  "father",
  "mainContact",
  "legalGuardian",
  "legalguardian",
]);

/**
 * Erziehungsberechtigten-Person-ID aus Stammdaten (explizit, Rechnungskontakt, Beziehungen).
 * @param {object|null|undefined} person
 * @returns {string}
 */
export function resolveLegalGuardianPersonId(person) {
  const p = person && typeof person === "object" ? person : {};

  const explicit = __asStr(p.legalGuardianPersonId ?? p.legal_guardian_person_id);
  if (explicit) return explicit;

  const billingContactId = __asStr(p.billingContactId ?? p.billing_contact_id);
  if (billingContactId) return billingContactId;

  const relationships = Array.isArray(p.relationships) ? p.relationships : [];
  const guardianRows = relationships.filter((row) => {
    const type = __asStr(row?.relationshipType ?? row?.relationship_type).toLowerCase();
    const related = __asStr(row?.relatedPersonId ?? row?.related_person_id);
    return related && GUARDIAN_RELATIONSHIP_TYPES.includes(type);
  });

  if (!guardianRows.length) return "";

  const main = guardianRows.find((row) => row?.isMainContact || row?.is_main_contact);
  const pick = main || guardianRows[0];
  return __asStr(pick?.relatedPersonId ?? pick?.related_person_id);
}

/**
 * @param {object|null|undefined} person
 * @returns {boolean}
 */
export function hasLegalGuardianOnRecord(person) {
  return !!resolveLegalGuardianPersonId(person);
}
